import { create } from "zustand";

import type { CorridorModel, MeasureKey, PlacementMeasures } from "../../api/types";
import type { LonLat } from "./corridorFrame";
import type { Overrides } from "./zoMeasures";

/** État fugace des gestes du mode édition, propre au paquet « carte » (le contrat partagé avec les panneaux reste
 *  `useManoeuvre`) : position du pointeur (sommets mineurs révélés à son approche), retour visuel du geste en
 *  cours (puce de largeur, trait vers l'autre limite, règle violée), annonce `aria-live` du parcours au clavier,
 *  aperçu de l'historique (plusieurs ⌘Z rapides : le fuseau suit tout de suite, docs/14 §2.8). Lignes de la ZO
 *  (docs/15 §4.3) : positions choisies d'un glissement en cours ou en attente de réponse, et celles que visent des
 *  annulations en attente d'envoi. */

/** Puce du geste : largeur d'une paire (sommet seul, ⌥ : largeur par défaut de la clé de gabarit, plage
 *  doctrinale, alerte selon la règle de docs/15 §2.4) ou règle violée (géométrie invalide). */
export type GestureTag =
  | {
      kind: "width";
      at: LonLat;
      width: number;
      doctrinal: number;
      range: readonly [number, number] | null;
      alert: boolean;
      snapped: boolean;
    }
  | { kind: "rule"; at: LonLat; rule: string };

interface GestureState {
  /** Pointeur au-dessus de la carte, en mode édition. */
  pointer: LonLat | null;
  tag: GestureTag | null;
  /** Trait tireté du pointeur au point le plus proche de l'autre limite (sommet seul). */
  tether: [LonLat, LonLat] | null;
  /** Annonce lue par les lecteurs d'écran (Tab / ⇧Tab sur les sommets). */
  announcement: string;
  /** Fuseau visé par des annulations en attente d'envoi (rendu côté client avant la réponse). */
  historyPreview: CorridorModel | null;
  /** Lignes de la ZO pendant un glissement (ou jusqu'à la réponse, B28) : positions choisies montrées à la place de
   *  celles du serveur ; les lignes calculées qui en dépendent suivent (`zoMeasures.relayout`). */
  measurePreview: Overrides | null;
  /** Lignes de la ZO visées par des annulations en attente d'envoi (`placement.measures`, null : positions
   *  calculées) ; null : aucun aperçu. */
  historyMeasures: { measures: PlacementMeasures | null } | null;
  /** Ligne de la ZO désignée sans puce montée (noms masqués) : halo qui pulse sur la ligne (ZoMeasuresLayers) ;
   *  `id` change à chaque désignation. */
  measureFlash: { key: MeasureKey; id: number } | null;
  setPointer: (pointer: LonLat | null) => void;
  setFeedback: (tag: GestureTag | null, tether?: [LonLat, LonLat] | null) => void;
  announce: (text: string) => void;
  setHistoryPreview: (corridor: CorridorModel | null) => void;
  setMeasurePreview: (overrides: Overrides | null) => void;
  setHistoryMeasures: (state: { measures: PlacementMeasures | null } | null) => void;
  flashMeasure: (key: MeasureKey) => void;
}

export const useGestureState = create<GestureState>()((set, get) => ({
  pointer: null,
  tag: null,
  tether: null,
  announcement: "",
  historyPreview: null,
  measurePreview: null,
  historyMeasures: null,
  measureFlash: null,
  setPointer: (pointer) => {
    const current = get().pointer;
    if (current === pointer || (current && pointer && current[0] === pointer[0] && current[1] === pointer[1])) return;
    set({ pointer });
  },
  setFeedback: (tag, tether = null) => set({ tag, tether }),
  announce: (announcement) => set({ announcement }),
  setHistoryPreview: (historyPreview) => set({ historyPreview }),
  setMeasurePreview: (measurePreview) => set({ measurePreview }),
  setHistoryMeasures: (historyMeasures) => set({ historyMeasures }),
  flashMeasure: (key) => set({ measureFlash: { key, id: (get().measureFlash?.id ?? 0) + 1 } }),
}));
